/**
 * React Query v5 persistence utilities for persisting cache across page refreshes
 * This implementation uses sessionStorage with user-scoped cache keys to prevent
 * data leakage between different users.
 *
 * Note: React Query v5 uses `gcTime` instead of `cacheTime` and requires React 18+
 */

import { QueryClient, QueryKey } from "@tanstack/react-query";

const CACHE_VERSION = 1; // Increment when cache structure changes
const CACHE_PREFIX = "react-query-cache";
const MAX_CACHE_AGE = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface CachedData {
    data: unknown;
    timestamp: number;
    version: number;
}

/**
 * Get cache key scoped by user ID to prevent cache collision between users
 * Falls back to session-based key if user ID is not available
 */
function getCacheKey(userId?: string | null): string {
    if (userId) {
        return `${CACHE_PREFIX}-v${CACHE_VERSION}-user-${userId}`;
    }
    // Fallback to session-scoped key if no user ID (for anonymous users)
    return `${CACHE_PREFIX}-v${CACHE_VERSION}-session`;
}

/**
 * Check if cached data is still valid based on age
 */
function isCacheValid(cached: CachedData): boolean {
    const age = Date.now() - cached.timestamp;
    return age < MAX_CACHE_AGE && cached.version === CACHE_VERSION;
}

/**
 * Persist React Query cache to sessionStorage
 */
export function persistQueryCache(queryClient: QueryClient, userId?: string | null): void {
    const cacheKey = getCacheKey(userId);

    try {
        const dehydratedState = queryClient.getQueryCache().getAll();
        const cacheData: Record<string, CachedData> = {};

        // Only persist successful queries (no errors)
        dehydratedState.forEach((query) => {
            if (query.state.data && query.state.status === "success") {
                // Only cache queries that have been successfully fetched
                if (query.state.dataUpdateCount > 0) {
                    const key = JSON.stringify(query.queryKey);
                    cacheData[key] = {
                        data: query.state.data,
                        timestamp: query.state.dataUpdatedAt || Date.now(),
                        version: CACHE_VERSION,
                    };
                }
            }
        });

        const serialized = JSON.stringify(cacheData);
        sessionStorage.setItem(cacheKey, serialized);
    } catch (error) {
        console.warn("Failed to persist React Query cache:", error);
    }
}

/**
 * Restore React Query cache from sessionStorage
 */
export function restoreQueryCache(queryClient: QueryClient, userId?: string | null): void {
    const cacheKey = getCacheKey(userId);

    try {
        const cached = sessionStorage.getItem(cacheKey);
        if (!cached) {
            return;
        }

        const cacheData: Record<string, CachedData> = JSON.parse(cached);

        // Restore valid queries
        Object.entries(cacheData).forEach(([key, cached]) => {
            if (!isCacheValid(cached)) {
                return; // Skip expired or version-mismatched cache
            }

            try {
                const queryKey: QueryKey = JSON.parse(key);

                // Set the cached data with appropriate metadata
                queryClient.setQueryData(queryKey, cached.data, {
                    updatedAt: cached.timestamp,
                });
            } catch (error) {
                console.warn(`Failed to restore query with key ${key}:`, error);
            }
        });
    } catch (error) {
        console.warn("Failed to restore React Query cache:", error);
        // Clear corrupted cache
        sessionStorage.removeItem(cacheKey);
    }
}

/**
 * Clear persisted cache for a specific user or all cache
 */
export function clearPersistedCache(userId?: string | null): void {
    if (userId) {
        const cacheKey = getCacheKey(userId);
        sessionStorage.removeItem(cacheKey);
    } else {
        // Clear all cache keys
        Object.keys(sessionStorage).forEach((key) => {
            if (key.startsWith(CACHE_PREFIX)) {
                sessionStorage.removeItem(key);
            }
        });
    }
}

/**
 * Clean up expired cache entries
 */
export function cleanupExpiredCache(): void {
    Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith(CACHE_PREFIX)) {
            try {
                const cached = sessionStorage.getItem(key);
                if (cached) {
                    const cacheData: Record<string, CachedData> = JSON.parse(cached);
                    const hasValidEntries = Object.values(cacheData).some(isCacheValid);

                    if (!hasValidEntries) {
                        // All entries expired, remove the cache
                        sessionStorage.removeItem(key);
                    }
                }
            } catch {
                // Invalid cache, remove it
                sessionStorage.removeItem(key);
            }
        }
    });
}
